import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  UserPlus,
  MessageSquarePlus,
  ExternalLink,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useAuth } from "../../utils/auth";
import {
  canViewTickets,
  canChangeTicketStatus,
  canAssignTickets,
  canEditTickets,
  canDeleteTickets,
} from "../../utils/roles";
import {
  fetchTicketById,
  updateTicketStatus,
  updateTicketPriority,
  assignTicket,
  addTicketNote,
  deleteTicket,
  fetchAssignableUsers,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
} from "../../api/TicketAPIs";
import { AttachmentPreview, TicketStatusBadge, TicketPriorityBadge } from "./TicketBadges";
import {
  ACTIVITY_LABELS_AR,
  PRIORITY_LABELS_AR,
  STATUS_LABELS_AR,
  formatDateTime,
  resolveMediaUrl,
} from "./ticketUi";

const RESOLVED_STATUSES = new Set(["Resolved", "Closed", "Invalid / No Issue Found"]);

const TicketDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canView = canViewTickets(authState.role);
  const canStatus = canChangeTicketStatus(authState.role);
  const canAssign = canAssignTickets(authState.role);
  const canEdit = canEditTickets(authState.role);
  const canDelete = canDeleteTickets(authState.role);

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [assignee, setAssignee] = useState("");
  const [note, setNote] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  const { data: ticket, isLoading, error, refetch } = useQuery({
    queryKey: ["ticket", id],
    queryFn: () => fetchTicketById(id),
    enabled: canView && !!id,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["ticketAssignableUsers"],
    queryFn: fetchAssignableUsers,
    enabled: canAssign,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!authState.isValidating && !canView) navigate("/login");
  }, [authState, canView, navigate]);

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setPriority(ticket.priority);
      setAssignee(ticket.assignedToUserId ? String(ticket.assignedToUserId) : "");
    }
  }, [ticket]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      setActionNote("");
      setNote("");
      refetch();
    } catch (err) {
      toast.error(err.message || "فشلت العملية");
    } finally {
      setBusy(false);
    }
  };

  if (!canView) return null;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500" dir="rtl">
        جاري التحميل...
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen p-8" dir="rtl">
        <div className="bg-red-50 text-red-700 p-4 rounded-xl">{error?.message || "التذكرة غير موجودة"}</div>
        <button onClick={() => navigate("/tickets")} className="mt-4 text-indigo-600">
          العودة للقائمة
        </button>
      </div>
    );
  }

  const orderLevelIssues = (ticket.issues || []).filter((i) => !i.orderDetailsId);
  const itemIssues = (ticket.issues || []).filter((i) => i.orderDetailsId);
  const alreadyDone = RESOLVED_STATUSES.has(ticket.status);

  const markResolved = () =>
    run(() => updateTicketStatus(ticket.id, "Resolved", actionNote || undefined), "تم تعليم التذكرة كمحلولة");

  const removeTicket = async () => {
    if (!window.confirm(`مسح التذكرة #${ticket.id}؟\nلا يمكن التراجع عن هذه العملية.`)) return;
    setBusy(true);
    try {
      await deleteTicket(ticket.id);
      toast.success("تم مسح التذكرة");
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticketStatusCounts"] });
      navigate("/tickets");
    } catch (err) {
      toast.error(err.message || "فشل مسح التذكرة");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate("/tickets")} className="p-2 rounded-xl bg-white shadow">
              <ChevronRight size={22} />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">تذكرة #{ticket.id}</h1>
              <p className="text-sm text-gray-500 mt-1">طلب #{ticket.orderId}</p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-xl bg-white border flex items-center gap-2 self-start"
          >
            <RefreshCw size={16} /> تحديث
          </button>
        </div>

        <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {canStatus && !alreadyDone && (
              <button
                type="button"
                disabled={busy}
                onClick={markResolved}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircle2 size={18} />
                اتحلت
              </button>
            )}
            {canDelete && alreadyDone && (
              <button
                type="button"
                disabled={busy}
                onClick={removeTicket}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 size={18} />
                مسح
              </button>
            )}
            <TicketStatusBadge status={ticket.status} />
            <button
              onClick={() => navigate(`/search-orders?orderId=${ticket.orderId}`)}
              className="mr-auto text-sm text-indigo-600 flex items-center gap-1"
            >
              فتح الطلب <ExternalLink size={14} />
            </button>
          </div>

          {canStatus && (
            <select
              value={status}
              disabled={busy}
              onChange={(e) => {
                const next = e.target.value;
                setStatus(next);
                if (next !== ticket.status) {
                  run(
                    () => updateTicketStatus(ticket.id, next, actionNote || undefined),
                    next === "Resolved" ? "تم تعليم التذكرة كمحلولة" : "تم تحديث الحالة"
                  );
                }
              }}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl bg-white"
            >
              {TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS_AR[s]}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5 space-y-4">
          <h2 className="text-xl font-bold">المشكلة</h2>

          {orderLevelIssues.map((issue) => (
            <div key={issue.id} className="border border-indigo-100 bg-indigo-50/40 rounded-2xl p-4">
              <div className="font-bold text-indigo-800 text-lg mb-1">
                {issue.problemTypeName || "مشكلة على مستوى الطلب"}
              </div>
              <p className="mt-2 text-gray-800 whitespace-pre-wrap">{issue.description}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {(issue.attachments || []).map((a) => (
                  <AttachmentPreview key={a.id} attachment={a} />
                ))}
              </div>
            </div>
          ))}

          {itemIssues.map((issue) => (
            <div key={issue.id} className="border border-gray-200 rounded-2xl p-4 space-y-3">
              <div className="font-bold text-gray-900 text-lg">{issue.problemTypeName || "مشكلة"}</div>
              <p className="text-gray-800 whitespace-pre-wrap">{issue.description}</p>
              <div className="flex flex-wrap gap-2">
                {issue.snapshotImageUrl ? (
                  <a href={resolveMediaUrl(issue.snapshotImageUrl)} target="_blank" rel="noreferrer">
                    <img
                      src={resolveMediaUrl(issue.snapshotImageUrl)}
                      alt=""
                      className="w-28 h-28 rounded-xl object-cover bg-gray-100"
                    />
                  </a>
                ) : null}
                {(issue.attachments || []).map((a) => (
                  <AttachmentPreview key={a.id} attachment={a} />
                ))}
              </div>
              {(issue.snapshotDesignCode || issue.snapshotDesignName || issue.snapshotProductModel) && (
                <div className="text-xs text-gray-500">
                  {issue.snapshotDesignCode || issue.snapshotDesignName}
                  {issue.snapshotProductModel ? ` — ${issue.snapshotProductModel}` : ""}
                  {issue.snapshotQuantity != null ? ` | الكمية: ${issue.snapshotQuantity}` : ""}
                </div>
              )}
            </div>
          ))}

          {(ticket.attachments || []).length > 0 && (
            <div>
              <div className="font-semibold mb-2">صور ومرفقات</div>
              <div className="flex flex-wrap gap-2">
                {ticket.attachments.map((a) => (
                  <AttachmentPreview key={a.id} attachment={a} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5">
          <button
            type="button"
            onClick={() => setShowExtra((v) => !v)}
            className="w-full flex items-center justify-between text-gray-700 font-semibold"
          >
            بيانات إضافية
            <ChevronDown size={18} className={showExtra ? "rotate-180 transition" : "transition"} />
          </button>

          {showExtra && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-400 text-xs">العميل</div>
                  <div className="font-medium">{ticket.customerName || "—"}</div>
                  <div className="text-gray-500">{ticket.customerPhone || ""}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">الأولوية</div>
                  <TicketPriorityBadge priority={ticket.priority} />
                </div>
                <div>
                  <div className="text-gray-400 text-xs">أنشئت بواسطة</div>
                  <div className="font-medium">{ticket.createdByEmail || "—"}</div>
                  <div className="text-gray-500 text-xs">{formatDateTime(ticket.createdAtUtc)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">مسندة إلى</div>
                  <div className="font-medium">{ticket.assignedToEmail || "غير مسندة"}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">حالة الطلب</div>
                  <div className="font-medium">{ticket.orderStatus || "—"}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs">آخر تحديث</div>
                  <div className="font-medium">{formatDateTime(ticket.updatedAtUtc)}</div>
                </div>
              </div>

              {canEdit && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                  <div>
                    <label className="text-xs text-gray-500">تغيير الأولوية</label>
                    <div className="flex gap-2 mt-1">
                      <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-xl"
                      >
                        {TICKET_PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {PRIORITY_LABELS_AR[p]}
                          </option>
                        ))}
                      </select>
                      <button
                        disabled={busy || priority === ticket.priority}
                        onClick={() =>
                          run(
                            () => updateTicketPriority(ticket.id, priority, actionNote || undefined),
                            "تم تحديث الأولوية"
                          )
                        }
                        className="px-3 py-2 rounded-xl bg-indigo-600 text-white disabled:opacity-40"
                      >
                        حفظ
                      </button>
                    </div>
                  </div>
                  {canAssign && (
                    <div>
                      <label className="text-xs text-gray-500 flex items-center gap-1">
                        <UserPlus size={14} /> تعيين
                      </label>
                      <div className="flex gap-2 mt-1">
                        <select
                          value={assignee}
                          onChange={(e) => setAssignee(e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-xl"
                        >
                          <option value="">إلغاء التعيين</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.email}
                            </option>
                          ))}
                        </select>
                        <button
                          disabled={busy}
                          onClick={() =>
                            run(
                              () =>
                                assignTicket(
                                  ticket.id,
                                  assignee ? Number(assignee) : null,
                                  actionNote || undefined
                                ),
                              "تم التعيين"
                            )
                          }
                          className="px-3 py-2 rounded-xl bg-indigo-600 text-white disabled:opacity-40"
                        >
                          حفظ
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                    placeholder="ملاحظة اختيارية مع التغيير"
                    className="md:col-span-2 w-full px-3 py-2 border rounded-xl"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5">
          <button
            type="button"
            onClick={() => setShowActivity((v) => !v)}
            className="w-full flex items-center justify-between text-gray-700 font-semibold mb-0"
          >
            سجل النشاط والملاحظات
            <ChevronDown size={18} className={showActivity ? "rotate-180 transition" : "transition"} />
          </button>

          {showActivity && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h2 className="sr-only">سجل النشاط والملاحظات</h2>

          {canEdit && (
            <div className="mb-5 flex flex-col md:flex-row gap-2">
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="أضف ملاحظة داخلية..."
                className="flex-1 px-3 py-2 border rounded-xl"
              />
              <button
                disabled={busy || !note.trim()}
                onClick={() => run(() => addTicketNote(ticket.id, note.trim()), "تمت إضافة الملاحظة")}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center gap-2 disabled:opacity-40 h-fit"
              >
                <MessageSquarePlus size={16} /> إضافة
              </button>
            </div>
          )}

          <div className="space-y-3">
            {(ticket.activities || []).map((a) => (
              <div key={a.id} className="relative pr-6 border-r-2 border-indigo-100 pb-3">
                <div className="absolute right-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-indigo-500" />
                <div className="font-semibold text-gray-800">
                  {ACTIVITY_LABELS_AR[a.activityType] || a.activityType}
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{a.description}</div>
                {(a.oldValue || a.newValue) && a.activityType !== "NoteAdded" && (
                  <div className="text-xs text-gray-500 mt-1">
                    {a.oldValue || "—"} → {a.newValue || "—"}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  {a.userEmail || "—"} — {formatDateTime(a.createdAtUtc)}
                </div>
              </div>
            ))}
            {!(ticket.activities || []).length && (
              <div className="text-gray-500 text-sm">لا يوجد نشاط بعد</div>
            )}
          </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsPage;
